import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { refreshSiteSettings } from '../../hooks/useSiteSettings';

// Aynı sistem farklı alan adlarından farklı marka ile açılır (veri ORTAK) -
// bkz. server/utils/brands.js. Buradan her markanın logo/isim/renk/metinleri
// düzenlenir; hangi alan adında hangi markanın görüneceği kodda sabittir.

const COLOR_FIELDS = [
  { key: 'primary_color', label: 'Ana renk', hint: 'Butonlar, aktif menü, vurgular' },
  { key: 'secondary_color', label: 'İkincil renk', hint: 'Geçişler (gradient), rozetler' },
  { key: 'tertiary_color', label: 'Üçüncü renk', hint: 'Bazı ekranlardaki çivit vurgular' },
  { key: 'login_accent_color', label: 'Giriş sayfası rengi', hint: 'Giriş/kayıt butonları ve arka plan ışıltıları' }
];

const TEXT_FIELDS = [
  { key: 'site_name', label: 'Site adı', hint: 'Sekme başlığı, logo yoksa giriş sayfasında görünen ad' },
  { key: 'login_title', label: 'Giriş sayfası başlığı', hint: 'Boş bırakılırsa gösterilmez' },
  { key: 'login_subtitle', label: 'Giriş sayfası alt metni', hint: 'Boşsa: "İşletmenizi yönetmenin modern yolu"' },
  { key: 'assistant_name', label: 'AI asistan adı', hint: 'Arayüzde AI asistan başlıklarında görünür' }
];

const card = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 20, marginBottom: 16 };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#e5e7eb', marginBottom: 4 };
const hintStyle = { fontSize: 11.5, color: '#9ca3af', marginTop: 4 };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(0,0,0,0.25)', color: '#fff', fontSize: 14, boxSizing: 'border-box' };

function BrandSettingsPanel({ token }) {
  const [brands, setBrands] = useState([]);
  const [selected, setSelected] = useState('');
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async (keepKey) => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/brands', { headers });
      setBrands(res.data);
      const key = keepKey || selected || res.data[0]?.key;
      setSelected(key);
      const b = res.data.find(x => x.key === key);
      if (b) setForm(b.settings);
    } catch (e) {
      alert(e.response?.data?.error || 'Marka ayarları yüklenemedi');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const selectBrand = (key) => {
    setSelected(key);
    const b = brands.find(x => x.key === key);
    if (b) setForm(b.settings);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      [...TEXT_FIELDS, ...COLOR_FIELDS].forEach(f => { payload[f.key] = form[f.key] ?? ''; });
      payload.site_logo_url = form.site_logo_url ?? '';
      payload.site_favicon_url = form.site_favicon_url ?? '';
      await axios.put(`/api/admin/brands/${selected}`, payload, { headers });
      await load(selected);
      refreshSiteSettings();
      alert('Marka ayarları kaydedildi.');
    } catch (e) {
      alert(e.response?.data?.error || 'Kaydedilemedi');
    }
    setSaving(false);
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`/api/admin/brands/${selected}/${kind}`, fd, { headers });
      setForm(f => ({ ...f, [kind === 'logo' ? 'site_logo_url' : 'site_favicon_url']: res.data.url }));
      refreshSiteSettings();
    } catch (e) {
      alert(e.response?.data?.error || 'Yüklenemedi');
    }
    setUploading('');
  };

  if (loading && !brands.length) return <div className="loading"><div className="loading-spinner"></div></div>;

  const current = brands.find(b => b.key === selected);
  const previewAccent = form.login_accent_color || '#2563eb';

  return (
    <div>
      <div className="info-box" style={{ marginBottom: 16 }}>
        Aynı sistem birden fazla alan adından açılabilir; veriler (firmalar, kullanıcılar, mesajlar) ORTAKTIR,
        sadece görünüm alan adına göre değişir. Değişiklikler kaydedildiği anda ilgili alan adında geçerli olur
        (açık sekmelerde sayfa yenilenince).
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {brands.map(b => (
          <button
            key={b.key}
            onClick={() => selectBrand(b.key)}
            className={selected === b.key ? 'btn-ai' : ''}
            style={selected === b.key ? { padding: '8px 16px' } : { padding: '8px 16px', background: 'rgba(255,255,255,0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, cursor: 'pointer' }}
          >
            {b.label}
          </button>
        ))}
      </div>

      {current && (
        <div style={{ ...card, fontSize: 13, color: '#9ca3af' }}>
          Bu marka şu alan adlarında görünür: {current.hosts.filter(h => h !== '127.0.0.1').map(h => <code key={h} style={{ marginRight: 8, color: '#e5e7eb' }}>{h}</code>)}
        </div>
      )}

      <div style={card}>
        <h3 style={{ marginTop: 0, color: '#fff', fontSize: 16 }}>Logo ve ikon</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {[
            { kind: 'logo', field: 'site_logo_url', label: 'Logo', hint: 'PNG/JPG/WEBP, en fazla 2 MB. Giriş sayfası ve sol menüde görünür.' },
            { kind: 'favicon', field: 'site_favicon_url', label: 'Favicon (sekme ikonu)', hint: 'PNG veya ICO, kare (ör. 64×64).' }
          ].map(item => (
            <div key={item.kind}>
              <span style={labelStyle}>{item.label}</span>
              <div style={{ height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', borderRadius: 10, marginBottom: 8, border: '1px dashed rgba(255,255,255,0.12)' }}>
                {form[item.field]
                  ? <img src={form[item.field]} alt={item.label} style={{ maxHeight: 56, maxWidth: '90%', objectFit: 'contain' }} />
                  : <span style={{ color: '#6b7280', fontSize: 12 }}>Yok</span>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <label style={{ flex: 1, textAlign: 'center', padding: '8px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', fontSize: 13 }}>
                  {uploading === item.kind ? 'Yükleniyor...' : 'Dosya yükle'}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/x-icon,.ico" style={{ display: 'none' }}
                    onChange={e => { upload(item.kind, e.target.files[0]); e.target.value = ''; }} />
                </label>
                {form[item.field] && (
                  <button onClick={() => setForm(f => ({ ...f, [item.field]: '' }))}
                    style={{ padding: '8px 10px', borderRadius: 10, background: 'transparent', border: '1px solid rgba(244,63,94,0.4)', color: '#f87171', cursor: 'pointer', fontSize: 13 }}>
                    Kaldır
                  </button>
                )}
              </div>
              <input style={{ ...inputStyle, marginTop: 8, fontSize: 12 }} placeholder="veya görsel URL'si yapıştırın"
                value={form[item.field] || ''} onChange={e => setForm(f => ({ ...f, [item.field]: e.target.value }))} />
              <div style={hintStyle}>{item.hint}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0, color: '#fff', fontSize: 16 }}>Renkler</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          {COLOR_FIELDS.map(f => (
            <div key={f.key}>
              <span style={labelStyle}>{f.label}</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={/^#[0-9a-f]{6}$/i.test(form[f.key] || '') ? form[f.key] : '#000000'}
                  onChange={e => setForm(s => ({ ...s, [f.key]: e.target.value }))}
                  style={{ width: 44, height: 40, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }} />
                <input style={inputStyle} value={form[f.key] || ''} placeholder="#rrggbb"
                  onChange={e => setForm(s => ({ ...s, [f.key]: e.target.value.trim() }))} />
              </div>
              <div style={hintStyle}>{f.hint}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16, padding: 16, borderRadius: 12, background: '#0c0f19', border: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#9ca3af', marginRight: 6 }}>Önizleme:</span>
          <span style={{ padding: '8px 14px', borderRadius: 10, background: form.primary_color, color: '#03121a', fontWeight: 700, fontSize: 13 }}>Ana buton</span>
          <span style={{ padding: '8px 14px', borderRadius: 10, background: `linear-gradient(135deg, ${form.primary_color}, ${form.secondary_color})`, color: '#fff', fontWeight: 700, fontSize: 13 }}>Geçiş</span>
          <span style={{ padding: '6px 12px', borderRadius: 999, border: `1px solid ${form.tertiary_color}`, color: form.tertiary_color, fontSize: 12 }}>Vurgu</span>
          <span style={{ padding: '8px 14px', borderRadius: 10, background: previewAccent, color: '#fff', fontWeight: 700, fontSize: 13 }}>Giriş Yap</span>
        </div>
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0, color: '#fff', fontSize: 16 }}>Metinler</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
          {TEXT_FIELDS.map(f => (
            <div key={f.key}>
              <span style={labelStyle}>{f.label}</span>
              <input style={inputStyle} value={form[f.key] || ''} onChange={e => setForm(s => ({ ...s, [f.key]: e.target.value }))} />
              <div style={hintStyle}>{f.hint}</div>
            </div>
          ))}
        </div>
      </div>

      <button className="btn-ai" onClick={save} disabled={saving} style={{ padding: '12px 28px' }}>
        {saving ? 'Kaydediliyor...' : 'Kaydet'}
      </button>
    </div>
  );
}

export default BrandSettingsPanel;
